import React, { useEffect, useState, useCallback } from "react";
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  message,
  Typography,
  Popconfirm,
  DatePicker,
  Row,
  Col,
  Descriptions,
  Timeline,
  Segmented,
} from "antd";
import {
  PlusOutlined,
  ArrowRightOutlined,
  EyeOutlined,
  StopOutlined,
  UserOutlined,
  EditOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import axiosInstance from "../../../services/apis";

const { Text } = Typography;
const { TextArea } = Input;

const PICKUP_STATUSES = [
  { key: "picked", label: "Picked", color: "processing" },
  { key: "in_transit", label: "In Transit", color: "warning" },
  { key: "delivered", label: "Delivered", color: "blue" },
  { key: "verify", label: "Verify", color: "purple" },
  { key: "tatmeen", label: "Tatmeen", color: "geekblue" },
  { key: "restored", label: "Restored", color: "success" },
  { key: "cancelled", label: "Cancelled", color: "error" },
];

const statusMap = Object.fromEntries(PICKUP_STATUSES.map((s) => [s.key, s]));

const NEXT_STATUS = {
  picked: "in_transit",
  in_transit: "delivered",
  delivered: "verify",
  verify: "tatmeen",
  tatmeen: "restored",
};

const DeliveryPickup = ({ authInfo }) => {
  const [pickups, setPickups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [createModal, setCreateModal] = useState(false);
  const [detailModal, setDetailModal] = useState(null);
  const [advanceLoading, setAdvanceLoading] = useState(false);
  const [form] = Form.useForm();
  const [branchUsers, setBranchUsers] = useState([]);

  // Customer selection state
  const [customerMode, setCustomerMode] = useState("existing");
  const [customers, setCustomers] = useState([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  const fetchPickups = useCallback(async () => {
    try {
      setLoading(true);
      const res = await axiosInstance.get("/delivery/pickup");
      setPickups(res.data?.data || []);
    } catch {
      message.error("Failed to load pickup requests");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBranchUsers = useCallback(async () => {
    try {
      const res = await axiosInstance.get("/delivery/branch-users");
      setBranchUsers(res.data?.data || []);
    } catch {}
  }, []);

  const fetchCustomers = useCallback(async () => {
    if (customers.length > 0) return;
    try {
      setCustomersLoading(true);
      const res = await axiosInstance.post("/settings/get-customers", {});
      setCustomers(res.data || []);
    } catch {
      message.error("Failed to load customers");
    } finally {
      setCustomersLoading(false);
    }
  }, [customers.length]);

  useEffect(() => {
    fetchPickups();
    fetchBranchUsers();
  }, [fetchPickups, fetchBranchUsers]);

  const handleOpenCreate = () => {
    setCustomerMode("existing");
    setSelectedCustomer(null);
    form.resetFields();
    fetchCustomers();
    setCreateModal(true);
  };

  const handleCustomerSelect = (accId) => {
    const customer = customers.find((c) => c.acc_id === accId);
    setSelectedCustomer(customer || null);
    if (customer) {
      form.setFieldsValue({
        customer_name: customer.acc_name || "",
        customer_phone: customer.contact_no || "",
      });
    } else {
      form.setFieldsValue({ customer_name: "", customer_phone: "" });
    }
  };

  const handleCreate = async () => {
    try {
      const values = await form.validateFields();
      const payload = {
        ...values,
        scheduled_date: values.scheduled_date
          ? values.scheduled_date.format("YYYY-MM-DD")
          : null,
      };

      if (customerMode === "existing" && selectedCustomer) {
        payload.customer_id = selectedCustomer.acc_id;
      }

      await axiosInstance.post("/delivery/pickup", payload);
      message.success("Pickup request created");
      setCreateModal(false);
      form.resetFields();
      setSelectedCustomer(null);
      fetchPickups();
    } catch (err) {
      if (err?.response?.data?.message)
        message.error(err.response.data.message);
    }
  };

  const handleAdvance = async (pickup) => {
    const nextStatus = NEXT_STATUS[pickup.status];
    if (!nextStatus) return;
    try {
      setAdvanceLoading(true);
      await axiosInstance.post(`/delivery/pickup/${pickup.id}/advance`);
      message.success(`Status advanced to: ${statusMap[nextStatus]?.label}`);
      if (detailModal?.id === pickup.id) {
        const res = await axiosInstance.get(`/delivery/pickup/${pickup.id}`);
        setDetailModal(res.data?.data || null);
      }
      fetchPickups();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to advance status");
    } finally {
      setAdvanceLoading(false);
    }
  };

  const handleCancel = async (id) => {
    try {
      await axiosInstance.post(`/delivery/pickup/${id}/cancel`);
      message.success("Pickup request cancelled");
      fetchPickups();
    } catch (err) {
      message.error(err?.response?.data?.message || "Failed to cancel");
    }
  };

  const openDetail = async (id) => {
    try {
      const res = await axiosInstance.get(`/delivery/pickup/${id}`);
      setDetailModal(res.data?.data || null);
    } catch {
      message.error("Failed to load details");
    }
  };

  const StatusTag = ({ status }) => {
    const s = statusMap[status];
    return s ? <Tag color={s.color}>{s.label}</Tag> : <Tag>{status}</Tag>;
  };

  const columns = [
    {
      title: "#",
      key: "idx",
      width: 50,
      render: (_, __, i) => i + 1,
    },
    {
      title: "Reference",
      dataIndex: "reference_no",
      render: (v) => v || "-",
    },
    {
      title: "Customer",
      dataIndex: "customer_name",
      render: (v) => v || "-",
    },
    {
      title: "Pickup Address",
      dataIndex: "pickup_address",
      ellipsis: true,
    },
    {
      title: "Assigned To",
      dataIndex: "assigned_user_name",
      render: (v) => v || <Text type="secondary">Unassigned</Text>,
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (v) => <StatusTag status={v} />,
    },
    {
      title: "Scheduled",
      dataIndex: "scheduled_date",
      render: (v) => (v ? dayjs(v).format("DD MMM YYYY") : "-"),
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => {
        const nextStatus = NEXT_STATUS[record.status];
        const nextLabel = nextStatus ? statusMap[nextStatus]?.label : null;
        return (
          <Space size="small">
            <Button
              size="small"
              icon={<EyeOutlined />}
              onClick={() => openDetail(record.id)}
            >
              View
            </Button>
            {nextLabel && (
              <Button
                size="small"
                type="primary"
                icon={<ArrowRightOutlined />}
                loading={advanceLoading}
                onClick={() => handleAdvance(record)}
              >
                {nextLabel}
              </Button>
            )}
            {record.status !== "cancelled" && record.status !== "restored" && (
              <Popconfirm
                title="Cancel this pickup request?"
                onConfirm={() => handleCancel(record.id)}
                okText="Yes"
                cancelText="No"
              >
                <Button size="small" danger icon={<StopOutlined />}>
                  Cancel
                </Button>
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const FlowSteps = () => (
    <Space wrap style={{ marginBottom: 0 }}>
      {PICKUP_STATUSES.filter((s) => s.key !== "cancelled").map((s, i, arr) => (
        <React.Fragment key={s.key}>
          <Tag color={s.color} style={{ fontSize: 12, padding: "2px 8px" }}>
            {s.label}
          </Tag>
          {i < arr.length - 1 && (
            <ArrowRightOutlined style={{ color: "#ccc" }} />
          )}
        </React.Fragment>
      ))}
    </Space>
  );

  const customerOptions = customers.map((c) => ({
    value: c.acc_id,
    label: c.acc_name,
    phone: c.contact_no,
  }));

  return (
    <div>
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
        }}
      >
        <div style={{ overflowX: "auto", maxWidth: "100%" }}>
          <FlowSteps />
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleOpenCreate}
        >
          New Pickup Request
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={pickups}
        loading={loading}
        rowKey="id"
        size="small"
        scroll={{ x: "max-content" }}
        pagination={{ pageSize: 20 }}
      />

      {/* ── Create Modal ── */}
      <Modal
        title="New Pickup Request"
        open={createModal}
        onOk={handleCreate}
        onCancel={() => {
          setCreateModal(false);
          form.resetFields();
          setSelectedCustomer(null);
        }}
        okText="Create"
        width={600}
        style={{ maxWidth: "95vw" }}
      >
        <Form form={form} layout="vertical">
          {/* ── Customer section ── */}
          <Form.Item label="Customer">
            <Segmented
              options={[
                { label: <Space><UserOutlined />Existing Customer</Space>, value: "existing" },
                { label: <Space><EditOutlined />New Customer</Space>, value: "new" },
              ]}
              value={customerMode}
              onChange={(val) => {
                setCustomerMode(val);
                setSelectedCustomer(null);
                form.setFieldsValue({ customer_name: "", customer_phone: "" });
              }}
              style={{ marginBottom: 12 }}
              block
            />

            {customerMode === "existing" ? (
              <Select
                showSearch
                allowClear
                placeholder="Search existing customer…"
                loading={customersLoading}
                options={customerOptions}
                filterOption={(input, option) =>
                  option?.label?.toLowerCase().includes(input.toLowerCase()) ||
                  option?.phone?.toLowerCase().includes(input.toLowerCase())
                }
                onChange={handleCustomerSelect}
                optionRender={(option) => (
                  <div>
                    <div>{option.data.label}</div>
                    {option.data.phone && (
                      <div style={{ fontSize: 11, color: "#888" }}>
                        {option.data.phone}
                      </div>
                    )}
                  </div>
                )}
                style={{ width: "100%" }}
              />
            ) : null}
          </Form.Item>

          {/* Name + phone shown for new customers; read-only preview for existing */}
          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_name" label="Customer Name">
                <Input
                  placeholder="Customer name"
                  readOnly={customerMode === "existing" && !!selectedCustomer}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="customer_phone" label="Phone">
                <Input
                  placeholder="Phone number"
                  readOnly={customerMode === "existing" && !!selectedCustomer}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="pickup_address"
            label="Pickup Address"
            rules={[{ required: true, message: "Pickup address is required" }]}
          >
            <TextArea rows={2} placeholder="Address to pick up from" />
          </Form.Item>

          <Row gutter={16}>
            <Col xs={24} sm={12}>
              <Form.Item name="assigned_user_id" label="Assign To">
                <Select
                  showSearch
                  allowClear
                  placeholder="Select person"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    (option?.children ?? "")
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                >
                  {branchUsers.map((u) => (
                    <Select.Option key={u.id} value={u.id}>
                      {u.full_name}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12}>
              <Form.Item name="scheduled_date" label="Scheduled Date">
                <DatePicker style={{ width: "100%" }} format="DD MMM YYYY" />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Additional notes" />
          </Form.Item>
        </Form>
      </Modal>

      {/* ── Detail Modal ── */}
      <Modal
        title="Pickup Request Details"
        open={!!detailModal}
        onCancel={() => setDetailModal(null)}
        footer={
          detailModal && NEXT_STATUS[detailModal.status] ? (
            <Button
              type="primary"
              icon={<ArrowRightOutlined />}
              loading={advanceLoading}
              onClick={() => handleAdvance(detailModal)}
            >
              Advance to {statusMap[NEXT_STATUS[detailModal.status]]?.label}
            </Button>
          ) : null
        }
        width={600}
        style={{ maxWidth: "95vw" }}
      >
        {detailModal && (
          <>
            <Descriptions
              column={{ xs: 1, sm: 2 }}
              size="small"
              bordered
              style={{ marginBottom: 16 }}
            >
              <Descriptions.Item label="Status" span={2}>
                <StatusTag status={detailModal.status} />
              </Descriptions.Item>
              <Descriptions.Item label="Customer">
                {detailModal.customer_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Phone">
                {detailModal.customer_phone || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Pickup Address" span={2}>
                {detailModal.pickup_address || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Assigned To">
                {detailModal.assigned_user_name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Scheduled">
                {detailModal.scheduled_date
                  ? dayjs(detailModal.scheduled_date).format("DD MMM YYYY")
                  : "-"}
              </Descriptions.Item>
              {detailModal.notes && (
                <Descriptions.Item label="Notes" span={2}>
                  {detailModal.notes}
                </Descriptions.Item>
              )}
            </Descriptions>

            {detailModal.history?.length > 0 && (
              <>
                <Text strong>Status History</Text>
                <Timeline
                  style={{ marginTop: 12 }}
                  items={detailModal.history.map((h) => ({
                    color:
                      statusMap[h.to_status]?.color === "success"
                        ? "green"
                        : "blue",
                    children: (
                      <div>
                        <Space>
                          {h.from_status && (
                            <Tag>
                              {statusMap[h.from_status]?.label || h.from_status}
                            </Tag>
                          )}
                          {h.from_status && (
                            <ArrowRightOutlined style={{ fontSize: 10 }} />
                          )}
                          <Tag color="blue">
                            {statusMap[h.to_status]?.label || h.to_status}
                          </Tag>
                        </Space>
                        <div style={{ fontSize: 11, color: "#888", marginTop: 2 }}>
                          {h.changed_by_name && `${h.changed_by_name} · `}
                          {dayjs(h.changed_at).format("DD MMM YYYY HH:mm")}
                          {h.notes && ` · ${h.notes}`}
                        </div>
                      </div>
                    ),
                  }))}
                />
              </>
            )}
          </>
        )}
      </Modal>
    </div>
  );
};

export default DeliveryPickup;
